import React from 'react';
import clsx from 'clsx';
import noDamagesImage from '@/assets/no-damages.png';

export interface NoDamageHistoryCardProps {
  /**
   * Główny nagłówek karty
   */
  title?: string;
  /**
   * Tekst opisowy pod nagłówkiem
   */
  description?: string;
  /**
   * Źródło obrazka (auto + plakietka). Domyślnie grafika "brak szkód".
   */
  imageSrc?: string;
  /**
   * Tekst alternatywny obrazka. Pusty = grafika dekoracyjna.
   */
  imageAlt?: string;
  /**
   * Dodatkowe klasy CSS
   */
  className?: string;
}

/**
 * Karta informująca, że pojazd nie posiada historii szkodowej.
 *
 * Layout responsywny:
 * - mobile: grafika na górze (wychodzi poza krawędź), tekst wyśrodkowany pod spodem
 * - desktop (md+): tekst po lewej, grafika absolutnie po prawej poza ramką
 *
 * @example
 * ```tsx
 * <NoDamageHistoryCard />
 * ```
 */
export const NoDamageHistoryCard: React.FC<NoDamageHistoryCardProps> = ({
  title = 'Nie wykazano historii szkodowej',
  description = 'W naszych bazach nie odnotowano szkód dla tego pojazdu',
  imageSrc = noDamagesImage,
  imageAlt = '',
  className,
}) => {
  const isDecorative = imageAlt.trim() === '';

  return (
    <div
      className={clsx(
        // mobile: kolumna, wyśrodkowana, grafika wychodzi ponad górną krawędź
        'relative flex flex-col items-center gap-3 overflow-visible rounded-2xl border border-legalStatus-successBorder bg-legalStatus-successBg px-6 pb-7 pt-8 text-center',
        // desktop: zielony box nie rezerwuje paddingiem miejsca na absolutną grafikę
        'md:min-h-[110px] md:flex-row md:items-center md:gap-0 md:p-0 md:text-left',
        className,
      )}
    >
      <div className="order-2 flex flex-col gap-1.5 md:order-1 md:w-[432px] md:flex-none md:justify-center md:gap-4 md:px-5 md:py-6">
        <p className="font-archivo text-lg font-bold leading-snug text-text-primary md:text-xl">
          {title}
        </p>
        <p className="font-inter text-sm leading-snug text-grayText">
          {description}
        </p>
      </div>

      <img
        src={imageSrc}
        alt={imageAlt}
        aria-hidden={isDecorative || undefined}
        className={clsx(
          'pointer-events-none order-1 w-[180px] select-none object-contain',
          // bleed ponad górną krawędź karty (mobile)
          '-mt-[5.5rem]',
          // desktop: grafika nie zajmuje miejsca w layoucie karty
          'md:absolute md:right-[-58px] md:top-1/2 md:order-none md:mt-0 md:w-[232px] md:-translate-y-1/2',
        )}
      />
    </div>
  );
};

export default NoDamageHistoryCard;
